<template>
  <div class="bill-flow-minute">
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1>分钟级资金流向</h1>
        <div class="title-decoration"></div>
      </div>
      <p class="page-subtitle">分钟级资金流向数据，精细追踪盘中资金变化</p>
    </div>

    <div class="filter-bar">
      <div class="filter-group">
        <label class="filter-label">股票代码</label>
        <el-input
          v-model="stockCode"
          placeholder="输入股票代码，如 002602"
          class="input-code"
          @keyup.enter="handleQuery"
        ></el-input>
      </div>

      <div class="filter-group">
        <label class="filter-label">日期</label>
        <el-date-picker
          v-model="selectedDate"
          type="date"
          placeholder="选择日期"
          value-format="yyyy-MM-dd"
          class="date-picker-single"
        ></el-date-picker>
      </div>

      <div class="filter-group">
        <label class="filter-label">开始时间</label>
        <el-time-picker
          v-model="startTime"
          placeholder="选择开始时间"
          value-format="HH:mm:ss"
          class="time-picker"
        ></el-time-picker>
      </div>

      <div class="filter-group">
        <label class="filter-label">结束时间</label>
        <el-time-picker
          v-model="endTime"
          placeholder="选择结束时间"
          value-format="HH:mm:ss"
          class="time-picker"
        ></el-time-picker>
      </div>

      <div class="button-group">
        <el-button type="primary" @click="handleQuery" :loading="loading">
          <i class="el-icon-search"></i>
          查询
        </el-button>
        <el-button @click="resetFilters">
          <i class="el-icon-refresh-left"></i>
          重置
        </el-button>
      </div>
    </div>

    <div v-if="billData.length > 0" class="bill-content">
      <!-- 统计信息 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-label">股票名称</div>
          <div class="stat-value">{{ billData[0]?.stock_name || '-' }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">数据点数</div>
          <div class="stat-value">{{ billData.length }} 条</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">主力净流入总计</div>
          <div class="stat-value" :class="totalMainInflow >= 0 ? 'positive' : 'negative'">
            {{ formatMoney(totalMainInflow) }}
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">超大单净流入总计</div>
          <div class="stat-value" :class="totalSuperInflow >= 0 ? 'positive' : 'negative'">
            {{ formatMoney(totalSuperInflow) }}
          </div>
        </div>
      </div>

      <!-- 图表 -->
      <div class="chart-section">
        <h3>资金流向曲线</h3>
        <div class="chart-container" ref="chartContainer"></div>
      </div>

      <!-- 数据表格 -->
      <div class="table-section">
        <h3>详细数据</h3>
        <el-table
          :data="paginatedData"
          border
          stripe
          style="width: 100%"
          :height="400"
        >
          <el-table-column
            prop="trade_time"
            label="时间"
            width="160"
          ></el-table-column>
          <el-table-column prop="main_net_inflow" label="主力净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.main_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.main_net_inflow) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="super_net_inflow" label="超大单净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.super_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.super_net_inflow) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="large_net_inflow" label="大单净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.large_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.large_net_inflow) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="medium_net_inflow" label="中单净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.medium_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.medium_net_inflow) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="small_net_inflow" label="小单净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.small_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.small_net_inflow) }}
              </span>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[20, 50, 100, 200]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="billData.length"
          class="pagination"
        >
        </el-pagination>
      </div>
    </div>

    <div v-else-if="!loading && hasSearched" class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <p>暂无数据</p>
      <p class="hint">请输入股票代码和日期范围进行查询</p>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'BillFlowMinute',
  data() {
    const today = new Date()
    const todayStr = this.formatDate(today)

    return {
      stockCode: '002602',
      selectedDate: todayStr,
      startTime: '13:30:00',
      endTime: '15:00:00',
      billData: [],
      loading: false,
      hasSearched: false,
      currentPage: 1,
      pageSize: 50,
      chart: null
    }
  },
  computed: {
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.billData.slice(start, end)
    },
    totalMainInflow() {
      return this.billData.reduce((sum, item) => sum + (item.main_net_inflow || 0), 0)
    },
    totalSuperInflow() {
      return this.billData.reduce((sum, item) => sum + (item.super_net_inflow || 0), 0)
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    async handleQuery() {
      if (!this.stockCode) {
        this.$message.warning('请输入股票代码')
        return
      }
      if (!this.selectedDate) {
        this.$message.warning('请选择日期')
        return
      }

      this.loading = true
      this.hasSearched = true

      try {
        const params = {
          stock_code: this.stockCode,
          date: this.selectedDate,
          start_time: this.startTime,
          end_time: this.endTime
        }

        const response = await axios.get('/stock/bill/today/query', { params })

        if (response.data.status === 'success') {
          this.billData = response.data.data || []
          if (this.billData.length > 0) {
            this.$nextTick(() => {
              this.renderChart()
            })
          }
          this.$message.success(`查询成功，共 ${this.billData.length} 条数据`)
        } else {
          this.$message.error(response.data.error || '查询失败')
        }
      } catch (error) {
        console.error('查询失败:', error)
        this.$message.error('查询失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    resetFilters() {
      const today = new Date()
      this.stockCode = '002602'
      this.selectedDate = this.formatDate(today)
      this.startTime = '13:30:00'
      this.endTime = '15:00:00'
      this.billData = []
      this.hasSearched = false
      this.currentPage = 1
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },

    renderChart() {
      if (!this.$refs.chartContainer) return

      if (this.chart) {
        this.chart.dispose()
      }

      this.chart = echarts.init(this.$refs.chartContainer)

      const times = this.billData.map(d => {
        const t = new Date(d.trade_time)
        return `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`
      })

      // 计算净流入变化量（相比前一分钟的变化量）
      const calculateChange = (data) => {
        return data.map((item, index) => {
          if (index === 0) return 0  // 第一个数据点设为0
          return item - data[index - 1]
        })
      }

      const superInflow = this.billData.map(item => item.super_net_inflow || 0)
      const largeInflow = this.billData.map(item => item.large_net_inflow || 0)
      const mediumInflow = this.billData.map(item => item.medium_net_inflow || 0)
      const smallInflow = this.billData.map(item => item.small_net_inflow || 0)
      const mainInflow = this.billData.map(item => item.main_net_inflow || 0)

      // 构建系列数据 - 双网格结构，使用变化量
      const series = [
        {
          name: '超大单净流入变化',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: calculateChange(superInflow),
          itemStyle: {
            color: '#d32f2f'
          }
        },
        {
          name: '大单净流入变化',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: calculateChange(largeInflow),
          itemStyle: {
            color: '#f57c00'
          }
        },
        {
          name: '中单净流入变化',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: calculateChange(mediumInflow),
          itemStyle: {
            color: '#fbc02d'
          }
        },
        {
          name: '小单净流入变化',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: calculateChange(smallInflow),
          itemStyle: {
            color: '#388e3c'
          }
        },
        {
          name: '主力净流入',
          type: 'bar',
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: mainInflow,
          itemStyle: {
            color: params => params.value >= 0 ? '#ef5350' : '#26a69a'
          }
        }
      ]

      const option = {
        title: {
          text: `${this.billData[0]?.stock_name || this.stockCode} 分钟级资金流向`,
          left: 'center',
          textStyle: { color: '#e2e8f0' }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' }
        },
        legend: {
          data: [
            {
              name: '超大单净流入变化',
              textStyle: { color: '#e2e8f0' },
              itemStyle: { color: '#d32f2f' }
            },
            {
              name: '大单净流入变化',
              textStyle: { color: '#e2e8f0' },
              itemStyle: { color: '#f57c00' }
            },
            {
              name: '中单净流入变化',
              textStyle: { color: '#e2e8f0' },
              itemStyle: { color: '#fbc02d' }
            },
            {
              name: '小单净流入变化',
              textStyle: { color: '#e2e8f0' },
              itemStyle: { color: '#388e3c' }
            },
            {
              name: '主力净流入',
              textStyle: { color: '#e2e8f0' },
              itemStyle: { color: '#ef5350' }
            }
          ],
          top: 30,
          backgroundColor: 'rgba(15, 23, 42, 0.8)',
          borderColor: 'rgba(99, 102, 241, 0.3)',
          borderWidth: 1,
          borderRadius: 8,
          padding: [12, 20]
        },
        grid: [
          { left: '8%', right: '8%', top: '12%', height: '38%' },
          { left: '8%', right: '8%', top: '63%', height: '28%' }
        ],
        xAxis: [
          {
            type: 'category',
            data: times,
            gridIndex: 0,
            axisLabel: {
              rotate: 0,
              color: '#94a3b8'
            }
          },
          {
            type: 'category',
            data: times,
            gridIndex: 1,
            axisLabel: {
              rotate: 45,
              color: '#94a3b8'
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            gridIndex: 0,
            name: '净流入变化(元)',
            nameTextStyle: { color: '#94a3b8' },
            axisLabel: {
              color: '#94a3b8',
              formatter: (value) => {
                if (value >= 100000000) return (value / 100000000).toFixed(1) + '亿'
                if (value >= 10000) return (value / 10000).toFixed(0) + '万'
                return value
              }
            },
            splitLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.1)' } }
          },
          {
            type: 'value',
            gridIndex: 1,
            name: '主力净流入(元)',
            nameTextStyle: { color: '#94a3b8' },
            axisLabel: {
              color: '#94a3b8',
              formatter: (value) => {
                if (value >= 100000000) return (value / 100000000).toFixed(1) + '亿'
                if (value >= 10000) return (value / 10000).toFixed(0) + '万'
                return value
              }
            },
            splitLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.1)' } }
          }
        ],
        dataZoom: [
          { type: 'inside', xAxisIndex: [0, 1], start: 0, end: 100 },
          { type: 'slider', xAxisIndex: [0, 1], start: 0, end: 100, height: 20, bottom: 10 }
        ],
        series
      }

      this.chart.setOption(option)

      window.addEventListener('resize', this.handleResize)
    },

    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    },

    formatMoney(value) {
      if (value === null || value === undefined) return '-'
      const num = Number(value)
      if (Math.abs(num) >= 100000000) return (num / 100000000).toFixed(2) + '亿'
      if (Math.abs(num) >= 10000) return (num / 10000).toFixed(2) + '万'
      return num.toFixed(2)
    },

    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },

    handleCurrentChange(val) {
      this.currentPage = val
    }
  }
}
</script>

<style scoped>
.bill-flow-minute {
  padding: 20px;
  width: 100%;
  background: transparent;
  color: #e2e8f0;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.title-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.title-icon svg {
  width: 24px;
  height: 24px;
}

.page-title h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.title-decoration {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, rgba(99, 102, 241, 0.5) 0%, transparent 100%);
}

.page-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  margin-left: 52px;
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  padding: 20px;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
}

.input-code {
  width: 160px;
}

.date-picker-single,
.time-picker {
  width: 160px;
}

.input-code :deep(.el-input__inner),
.date-picker-single :deep(.el-input__inner),
.time-picker :deep(.el-input__inner) {
  background: rgba(15, 23, 42, 0.8);
  border-color: rgba(99, 102, 241, 0.3);
  color: #e2e8f0;
}

.button-group {
  display: flex;
  gap: 10px;
  margin-left: auto;
}

.bill-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.stat-card {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.stat-label {
  font-size: 13px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #e2e8f0;
}

.stat-value.positive {
  color: #ef4444;
}

.stat-value.negative {
  color: #22c55e;
}

.chart-section {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 20px;
}

.chart-section h3 {
  margin: 0 0 16px 0;
  color: #e2e8f0;
  font-size: 16px;
}

.chart-container {
  width: 100%;
  height: 500px;
}

.table-section {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 20px;
}

.table-section h3 {
  margin: 0 0 16px 0;
  color: #e2e8f0;
  font-size: 16px;
}

.table-section :deep(.el-table) {
  background: transparent;
}

.table-section :deep(.el-table th) {
  background: rgba(15, 23, 42, 0.8);
  color: #e2e8f0;
  border-color: rgba(99, 102, 241, 0.15);
}

.table-section :deep(.el-table td) {
  border-color: rgba(99, 102, 241, 0.15);
}

.table-section :deep(.el-table tr:hover > td) {
  background: rgba(99, 102, 241, 0.1);
}

.table-section .positive {
  color: #ef4444;
}

.table-section .negative {
  color: #22c55e;
}

.pagination {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.pagination :deep(.el-pagination) {
  color: #94a3b8;
}

.pagination :deep(.el-pagination .el-pager li) {
  background: rgba(15, 23, 42, 0.6);
  border-color: rgba(99, 102, 241, 0.2);
  color: #cbd5e1;
}

.pagination :deep(.el-pagination .el-pager li:hover) {
  border-color: rgba(99, 102, 241, 0.5);
  color: #a5b4fc;
}

.pagination :deep(.el-pagination .el-pager li.active) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-color: transparent;
  color: #fff;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
}

.empty-state svg {
  width: 64px;
  height: 64px;
  color: #475569;
  margin-bottom: 16px;
}

.empty-state p {
  margin: 8px 0;
  color: #64748b;
}

.empty-state .hint {
  font-size: 14px;
}
</style>
